import { Lightbulb, X } from "lucide-react";
import { Button } from "../ui/button";

/**
 * Friendly one-time hint shown on first Evaluation/Daily-tab visit.
 * Examples are tailored to the therapist's discipline (first two field labels).
 */
export default function VoiceRoutingHint({
  labels = [],
  onDismiss,
  tabName = "Evaluation",
  testIdPrefix = "voice-routing-hint",
}) {
  const [a, b] = labels;
  const example1 = a || "Assessment";
  const example2 = b || "Objective";

  return (
    <div
      data-testid={testIdPrefix}
      className="relative rounded-3xl border border-[#A7F3D0] bg-gradient-to-br from-[#ECFDF5] via-white to-[#F0FDF4] p-5 md:p-6 shadow-[0_10px_30px_rgba(16,185,129,0.12)]"
    >
      <button
        data-testid={`${testIdPrefix}-close-icon`}
        onClick={onDismiss}
        aria-label="Dismiss hint"
        className="absolute top-3 right-3 inline-flex items-center justify-center w-7 h-7 rounded-full text-[#94A3B8] hover:text-[#0F172A] hover:bg-white/70 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-4">
        <span className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-[#10B981] text-white shadow-[0_6px_14px_rgba(16,185,129,0.4)]">
          <Lightbulb className="w-5 h-5" strokeWidth={2.5} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-figtree text-[11px] uppercase tracking-[0.18em] font-semibold text-[#065F46]">
            {tabName} · voice routing tip
          </div>
          <h3 className="mt-1 font-outfit text-base md:text-lg font-bold tracking-tight text-[#0F172A]">
            Say a section name and TherapyNote routes the next words into it.
          </h3>
          <p className="mt-1.5 font-figtree text-sm text-[#475569] leading-relaxed">
            Just say the field name first, then dictate normally. Try:
          </p>
          <ul className="mt-3 space-y-1.5">
            <li className="font-figtree text-sm text-[#0F172A]">
              <span className="inline-block px-2 py-0.5 rounded-md bg-white border border-[#D1FAE5] text-[#065F46] font-mono text-[12px] mr-2">
                “{example1}…”
              </span>
              followed by your findings flows into {example1}.
            </li>
            <li className="font-figtree text-sm text-[#0F172A]">
              <span className="inline-block px-2 py-0.5 rounded-md bg-white border border-[#D1FAE5] text-[#065F46] font-mono text-[12px] mr-2">
                “Section {example2.toLowerCase()}…”
              </span>
              switches the next words into {example2}.
            </li>
            <li className="font-figtree text-sm text-[#0F172A]">
              <span className="inline-block px-2 py-0.5 rounded-md bg-white border border-[#D1FAE5] text-[#065F46] font-mono text-[12px] mr-2">
                “Note {example1.toLowerCase()}…”
              </span>
              also works — say a section anytime to re-route mid-sentence.
            </li>
          </ul>
          <div className="mt-4">
            <Button
              data-testid={`${testIdPrefix}-dismiss-btn`}
              onClick={onDismiss}
              className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-sm font-medium px-5"
            >
              Got it
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
